import { FocusEventHandler, PropsWithChildren, ReactElement } from 'react';

import { IconProps } from '@/components/global/Icon';

import { AuthReducerPropsType } from './authModels';
import { ShareStats } from './campaignModels';
import { CompanyAccount } from './companyModels';
import { CustomTab, HeaderCustomization, MetaCustomization, PortalCustomizations } from './customizationModels';

/* Server Status Types */
export enum ServerStatus {
  'IDLE',
  'FETCH',
  'FETCHING',
  'FETCH_ERROR',
}

/* Layout Types */
export type CardProps = {
  children: ReactElement | ReactElement[];
  extraClass?: string;
  extraClassHolder?: string;
  title?: string;
  loading?: boolean;
  comingSoon?: boolean;
  options?: JSX.Element | ReactElement;
};

export type LayoutPropsType = {
  auth: AuthReducerPropsType;
  layout: LayoutReducerPropsType;
  metaCustomization?: MetaCustomization;
  customizationStatus?: ServerStatus;
  doCustomizeMetaInfo?: Function;
  nonce?: string;
  company?: CompanyAccount;
} & PropsWithChildren;

export type LayoutConsumePropsType = {
  children: ReactElement;
  menuActive: boolean;
  itemActive?: string[] | string;
  doActiveLayout: Function;
  doActiveLayoutItem: Function;
};
export type LayoutHeaderProps = {
  children: ReactElement;
};
export type LayoutContentProps = {
  children: ReactElement | ReactElement[];
  withHeader?: boolean;
};
export type LayoutScrollerProps = { children: ReactElement | ReactElement[]; withHeader?: boolean };
/* Ambassador Error */
export type AmbassadorError = {
  error?: Error;
  message: string;
  cause: string;
  source?: string;
  stack?: string;
  apiResponse?: object;
};

/* Menu Types */
export type MenuItemType = {
  tooltip?: string;
  icon?: string;
  keyName: string;
  itemText: string; // default menu text
  link: string;
  extraClass?: string;
  external?: boolean;
  showItemKeyName: string; //the name of customization property to control show/hide
  showItem?: boolean;
  itemCustomTextKeyName: string; //the name of customization property to modify menu text
  itemCustomText?: string;
};

export type SideBarPropsType = {
  itemActive: string[];
  items?: MenuItemType[];
  customization?: PortalCustomizations;
  customizationStatus?: ServerStatus;
  doCustomizeSideBar?: Function;
  mobileSidebarToggler?: boolean;
  setMobileSideBarToggler?: Function;
  onClickCallback?: Function;
  customTabs?: CustomTab[];
};

export type SideBarUserInfoPropsType = {
  userInfo: SideBarUserData;
  uid?: number;
  doSaveAmbassador: Function;
  saveAmbassadorStatus?: ServerStatus;
};

export enum ShareStatItem {
  'SHARES',
  'CLICKS',
  'REFERRALS',
}

export type SideBarUserData = {
  avatar_url: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  username: string;
  memorable_url: string;
  facebook_url: string;
  twitter_url: string;
  linkedin_url: string;
  pinterest_url: string;
};

/* Stats Item Types */
export enum StatItemVariant {
  POINTS_EARNED,
  POINTS_AVAILABLE,
  POINTS_REDEEMED,
  POINTS_EXPIRED,
  EARNED,
  AWAITING_APPROVE,
  AWAITING_PAYOUT,
  PAID,
  LOYALTY_CASH_AVAILABLE,
  LOYALTY_CASH_REDEEMED,
  LOYALTY_CASH_EXPIRED,
  LOYALTY_CASH_EARNED,
  EARNED_FIRST_PURCHASE,
  EARNED_RETURNING,
  REWARDS_AVAILABLE,
  REWARDS_REDEEMED,
  REWARDS_CASH_EARNED,
  REWARDS_CASH_FULFILLED,
  REWARDS_CASH_BALANCE,
  REWARDS_POINTS_EARNED,
  REWARDS_POINTS_FULFILLED,
  REWARDS_POINTS_BALANCE,
  REWARDS_CASH_REDEEMED,
  REWARDS_CASH_EXPIRED,
  REWARDS_POINTS_REDEEMED,
  REWARDS_POINTS_EXPIRED,
}

export type StateItemType = 'points' | 'cash' | 'commission' | 'rewards';

export type StatItemType = {
  className?: string;
  currency?: string;
  description?: string;
  icon: string;
  isLoyalty?: boolean;
  isAdmin3?: boolean;
  money?: number;
  moneyKey?: string;
  name: string;
  points?: number;
  pointsKey?: string;
  type: StateItemType; //enum
  variant: StatItemVariant;
  dataFromStatsEnpoint?: boolean;
  titleKey?: string;
};

export type StatsDashboardData = {
  total_money_earned: number; // -> earned
  money_pending: number; // -> awaiting approval
  current_commission_balance: number; // -> awaiting payout
  money_paid: number; // -> paid to date
  currency: string;
  total_points_earned: number;
  point_name: string;
  has_non_monetary: boolean;
  commission_type?: string;
};
export type ShareStatsBannerPropsType = {
  items?: ShareStatWidgetType[];
  shareStats: ShareStats;
  pointsEarned: number;
  moneyEarned: number;
  isLoading?: boolean;
  selectedStats?: ShareStatItem[];
};
export type ShareStatWidgetType = {
  keyName: string;
  statType: ShareStatItem; //enum
  statNumber: number;
  statLabel: string;
  bgColor: string;
  headerTabColor: string;
  isMonetary: boolean;
};

/* AmbaButton Types */
interface commonAmbaButtonProps {
  id?: string;
  onClick?: Function;
  disabled?: boolean;
  isSubmit?: boolean;
  content?: JSX.Element;
  text: string;
  extraClass?: string;
  extraTextClass?: string;
  extraConfirmationClass?: string;
  extraIconClass?: string;
  type?: AmbaButtonTypes;
  icon?: string;
  notifications?: string;
  notificationsClass?: string;
  withConfirmation?: boolean;
  showLoader?: boolean;
  confirmationText?: string;
}
type AmbaButtonProps = {
  isCustom: false;
  customBgColor?: never;
  customTxtColor?: never;
};
type CustomAmbaButtonProps = {
  isCustom?: true;
  customBgColor?: string | undefined;
  customTxtColor?: string | undefined;
  type?: AmbaButtonTypes;
};

export type AmbaButtonPropsType = commonAmbaButtonProps & (AmbaButtonProps | CustomAmbaButtonProps);

/* AmbaButton Types */
export enum AmbaButtonTypes {
  'TRANSPARENT',
  'VERMILION',
  'SHAKESPEARE',
  'WHITE',
  'APPLE',
  'ALTO',
  'REGENT',
  'NEPAL',
}

export enum ColorTypes {
  'shakespeare' = 'shakespeare',
  'danger' = 'danger',
  'info' = 'info',
  'success' = 'success',
  'warning' = 'warning',
}

/* AmbaInput Types */
export type AmbaInputPropsType = {
  id: string;
  type: AmbaInputTypes;
  onChange?: Function;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
  validator?: Function;
  ariaDescribedBy?: string;
  disabled?: boolean;
  placeHolder?: string;
  value: any;
  extraClass?: string;
  extraIconClass?: string;
  extraLabelClass?: string;
  icon?: IconProps['icon'];
  labelText?: string;
  autoComplete?: string;
  hasValidation?: boolean;
  hasLiveValidation?: boolean;
  validationMessage?: string;
  isRequired?: boolean;
  onValidationFail?: Function;
  onValidationSuccess?: Function;
  isValidField?: boolean;
  name?: string;
};
export enum AmbaInputTypes {
  'TEXT' = 'text',
  'EMAIL' = 'email',
  'PASSWORD' = 'password',
  'NUMBER' = 'number',
  'DATE' = 'date',
}

/* AmbaDropdown Types */
export type AmbaDropdownPropsType = {
  id: string;
  options: AmbaDropdownItem[];
  onClick?: Function;
  ariaDescribedBy?: string;
  disabled?: boolean;
  placeHolder?: string;
  value: string | undefined | boolean | number;
  extraClass?: string;
  labelText?: string;
  extraLabelClass?: string;
  bgColor?: string;
  width?: string;
  isRequired?: boolean;
  validationMessage?: string;
  hasValidation?: boolean;
  validator?: Function;
};

export type AmbaDropdownItem = {
  itemText: string;
  itemValue: any;
  itemTextKeyName: string | number; // used as unique map render key
  itemCategory?: string; // used for grouping/sorting if needed
  extraItemClass?: string;
};

/* AmbaImage Types */
interface AmbaImageCommonProps {
  imageSrc: string | undefined;
  alt: string;
  fallbackSrc?: string;
  extraClass?: string;
}
type AmbaImageLinkProps = {
  isLink: true;
  targetUrl: string;
};
type AmbaImageProps = {
  isLink: false;
  targetUrl?: never;
};
export type AmbaImagePropsType = AmbaImageCommonProps & (AmbaImageProps | AmbaImageLinkProps);

/* AmbaAlert Types */
export type AmbaAlertPropsType = {
  type?: AmbaAlertTypes;
  alertMessage: string;
  showAlert: boolean;
  showIcon?: boolean;
  ariaDescribedBy?: string;
  isDismiss?: boolean; //adds a close btn to dismiss the alert
  // TODO: create logic for linked alerts
  isLink?: boolean;
  linkUrl?: string;
  data?: any;
};

export enum AmbaAlertTypes {
  'NOTIFICATION' = 'primary',
  'ERROR' = 'danger',
  'WARNING' = 'warning',
  'SUCCESS' = 'success',
}

export type LayoutReducerPropsType = {
  itemActive: string[];
  layoutActive: boolean;
};

export type ConditionalWrapperPropsType = {
  condition: any;
  wrapper: any;
  children: HTMLElement | ReactElement;
};

export type HeaderPropsType = {
  customizationStatus?: ServerStatus;
  customization?: HeaderCustomization;
  companyName?: string;
  doCustomizeHeader?: Function;
  mobileSidebarToggler: boolean;
  setMobileSideBarToggler: Function;
};

/* States Options */
export type StateChoice = {
  name: string;
  value: string;
};

/* Redux Object Dispatch */
export type DispatchObject<T> = {
  ({ type, payload }: { type: string; payload: T }): any;
};
export type DispatchEmptyObject = {
  ({ type }: { type: string }): any;
};

/* Paginator */
export type Paginator<T> = {
  count: number;
  next: string | null;
  page_number?: number;
  previous: string | null;
  results: T[];
};

export enum PasswordStrengthLabel {
  WEAK = 'weak',
  ACCEPTABLE = 'acceptable',
  STRONG = 'strong',
}

export enum MetricIntervalTypes {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
}
